import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { ApiError } from "@/shared/api/http-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Slider } from "@/shared/components/ui/slider";
import type { PendingVenue } from "./types";
import { useApproveVenue } from "./use-approve-venue";

const DEFAULT_RATING = 4;

interface ApproveVenueDialogProps {
  readonly venue: PendingVenue | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ApproveVenueDialog({
  venue,
  open,
  onOpenChange,
  onSuccess,
}: ApproveVenueDialogProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(DEFAULT_RATING);
  const [error, setError] = useState<string | null>(null);
  const approve = useApproveVenue();

  // Reset form state when venue changes or dialog reopens
  useEffect(() => {
    if (open) {
      setRating(DEFAULT_RATING);
      setError(null);
    }
  }, [venue?.id, open]);

  if (!venue) return null;

  const handleApprove = () => {
    setError(null);
    approve.mutate(
      { venueId: venue.id, input: { googleMapsRating: rating } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess();
        },
        onError: (err) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            // TODO(API-0.3): when MFA enforcement lands, branch on
            // body.code === "MFA_REQUIRED" to show a re-auth prompt
            // instead of a generic forbidden message.
            setError(t("venues.errors.forbidden"));
          } else {
            setError(t("venues.approve.error"));
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="approve-venue-dialog">
        <DialogHeader>
          <DialogTitle>{t("venues.approve.title")}</DialogTitle>
          <DialogDescription>
            {t("venues.approve.description", { name: venue.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            {/* M-05: Associate label with slider via htmlFor/id */}
            <label
              htmlFor="rating-slider"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("venues.approve.ratingLabel")}
            </label>
            <div className="flex items-center gap-4">
              <Slider
                id="rating-slider"
                data-testid="rating-slider"
                min={1}
                max={5}
                step={0.5}
                value={[rating]}
                // M-03: Guard against empty value array
                onValueChange={(values) => {
                  if (values[0] !== undefined) setRating(values[0]);
                }}
                className="flex-1"
              />
              <div className="flex items-center gap-1 rounded-md bg-muted px-3 py-1.5">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span className="min-w-[2ch] text-center font-sans text-sm font-semibold">
                  {rating}
                </span>
              </div>
            </div>
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("venues.approve.ratingHint")}
            </p>
          </div>

          {error && (
            <p
              data-testid="approve-error"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={approve.isPending}
          >
            {t("venues.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-approve-button"
            onClick={handleApprove}
            disabled={approve.isPending}
          >
            {approve.isPending ? t("venues.approve.approving") : t("venues.actions.approve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
